'use client';

import { BookOpen } from 'lucide-react';

import type { TaskFile } from '@/entities/interview-task';

import { type AttachStatus, STATUS_PALETTE } from '../lib/status-palette';
import clsx from 'clsx';

type FileCardProps = {
	file: TaskFile;
	onOpen: (file: TaskFile) => void;
	status?: AttachStatus | null;
	compact?: boolean;
};

const isBook = (kind: TaskFile['kind']) =>
	kind === 'theory' || kind === 'markdown';

const getVersion = (file: TaskFile): number =>
	'version' in file ? file.version : 1;

export function FileCard({ file, onOpen, status, compact }: FileCardProps) {
	const statusStyle = status ? STATUS_PALETTE[status] : null;
	const version = getVersion(file);

	return (
		<button
			type="button"
			onClick={() => onOpen(file)}
			className={clsx(
				`flex cursor-pointer items-center gap-3.5 rounded-lg border border-gray-4 bg-gray-4/30 py-3 text-left font-sans transition-all duration-150 hover:bg-gray-4/50`,
				compact ? 'max-w-full px-4' : 'w-full pl-4 pr-3.5',
			)}
		>
			<div className="relative h-[50px] w-10 shrink-0">
				<svg
					width="40"
					height="50"
					viewBox="0 0 40 50"
					fill="none"
					className="block"
				>
					<path
						d="M3.5 4 C3.5 2 5 0.5 7 0.5 L26 0.5 L39.5 14 L39.5 46 C39.5 48 38 49.5 36 49.5 L7 49.5 C5 49.5 3.5 48 3.5 46 Z"
						className="fill-white stroke-gray-4"
						strokeWidth="1"
					/>
					<path
						d="M26 0.5 L26 11 C26 12.66 27.34 14 29 14 L39.5 14"
						className="fill-gray-5 stroke-gray-4"
						strokeWidth="1"
						strokeLinejoin="round"
					/>
				</svg>
				<div className="absolute inset-0 flex items-center justify-center pt-1.5">
					{isBook(file.kind) ? (
						<BookOpen size={14} strokeWidth={2} className="text-gray-3" />
					) : (
						<span className="font-mono text-[11px] font-semibold tracking-tight text-gray-3">
							{'</>'}
						</span>
					)}
				</div>
			</div>

			<div className="min-w-0 flex-1">
				<span className="block truncate text-base font-medium text-black">
					{file.name}
				</span>
				{(version > 1 || statusStyle) && (
					<div className="mt-0.5 flex items-center gap-2.5">
						{version > 1 && (
							<span className="text-sm text-gray-4">
								Отправка {version - 1}
							</span>
						)}
						{statusStyle && (
							<span className={`text-sm font-medium ${statusStyle.cls}`}>
								{statusStyle.label}
							</span>
						)}
					</div>
				)}
			</div>

			{!compact && (
				<div className="flex shrink-0 items-center rounded-md border border-gray-4 bg-white px-3 py-1.5 text-sm font-medium text-black">
					Открыть
				</div>
			)}
		</button>
	);
}
